import React from 'react';
import { Button, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { useAppLifecycle, type AppStatus } from '../../hooks/useAppLifecycle';

interface AppLifecycleControlProps {
  projectId: string | undefined;
}

const STATUS_CONFIG: Record<
  AppStatus,
  { icon: IconName; label: string; spin?: boolean }
> = {
  stopped:    { icon: IconName.Run,       label: 'Start App' },
  starting:   { icon: IconName.Loading,   label: 'Starting...', spin: true },
  running:    { icon: IconName.Stop,      label: 'Stop App' },
  stopping:   { icon: IconName.Loading,   label: 'Stopping...', spin: true },
  partial:    { icon: IconName.Run,       label: 'Start App' },
  error:      { icon: IconName.Run,       label: 'Start App' },
  no_compose: { icon: IconName.Container, label: 'No Docker Compose' },
  unknown:    { icon: IconName.Container, label: 'Checking...' },
};

const AppLifecycleControl: React.FC<AppLifecycleControlProps> = ({
  projectId,
}) => {
  const { state, startApp, stopApp, isTransitioning } = useAppLifecycle(projectId);

  const config = STATUS_CONFIG[state.status] ?? STATUS_CONFIG.unknown;

  const handleClick = async () => {
    if (isTransitioning) return;
    if (state.status === 'running' || state.status === 'partial') {
      await stopApp();
    } else if (
      state.status === 'stopped' ||
      state.status === 'error' ||
      state.status === 'unknown'
    ) {
      await startApp();
    }
  };

  if (!projectId || state.status === 'no_compose') {
    return null;
  }

  return (
    <Button
      label={
        <Icon
          name={config.icon}
          size='sm'
          className='text-fg-secondary'
          spin={config.spin}
          inheritColor
        />
      }
      variant='ghost'
      size='small'
      title={config.label}
      className='!rounded-full !p-bg-v2-lg'
      onClick={handleClick}
      disabled={isTransitioning}
    />
  );
};

export default AppLifecycleControl;
